import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Gamepad2, Book, Palette, Compass, ChevronRight, X, Rocket, UserPlus, Crown } from "lucide-react";
import axios from "axios";
import { useApp, API } from "../context";
import { isAdminUser } from "../utils/adminCheck";
import { IsaiahMascotCore } from "./isaiah";

const STORAGE_KEY = "isaiah_welcome_shown";

const WelcomeNewUserModal = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  // Idle auto-close: the post-signup welcome sits on top of the dashboard,
  // so if the parent doesn't interact we gently close it after a few seconds
  // (it was covering the "Add an Explorer" CTA underneath). Any interaction
  // cancels the timer so engaged users keep the guided flow.
  const [autoCloseIn, setAutoCloseIn] = useState(null);
  const engagedRef = useRef(false);

  useEffect(() => {
    // Show only if user just signed up (flag set by SignupPage)
    const justSignedUp = sessionStorage.getItem("isaiah_just_signed_up");
    const alreadyShown = localStorage.getItem(STORAGE_KEY);
    // Coordinate with KidOnboarding, wait if it's active for this kid
    const kidOnboardingActive = sessionStorage.getItem("isaiah_kid_onboarding_active");
    if (kidOnboardingActive) return;
    if (!justSignedUp || alreadyShown || !user || user.isDemo) return;
    // Account-level guard: never re-show once seen on ANY device/browser.
    let cancelled = false;
    let timer;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/onboarding-flags`);
        if (cancelled || r.data?.welcome_modal_seen) return;
      } catch (_) { /* on error, fall back to the local one-time flags */ }
      timer = setTimeout(() => { if (!cancelled) setVisible(true); }, 500);
    })();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [user]);

  // Tell PlatformTour (and any other ambient narrator) that the Welcome
  // modal owns the screen. This prevents Isaiah's tour voice from playing
  // behind the modal, the user reported hearing narration about a page
  // they couldn't see because it was hidden behind this popup.
  useEffect(() => {
    if (visible) {
      sessionStorage.setItem("isaiah_welcome_open", "1");
    } else {
      sessionStorage.removeItem("isaiah_welcome_open");
    }
    return () => sessionStorage.removeItem("isaiah_welcome_open");
  }, [visible]);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, "true");
    sessionStorage.removeItem("isaiah_just_signed_up");
    sessionStorage.removeItem("isaiah_welcome_open");
    // Persist account-wide so it never auto-shows again on any device.
    axios.post(`${API}/auth/onboarding-flag`, { flag: "welcome_modal_seen" }).catch(() => {});
  };

  const goExplore = (path) => {
    dismiss();
    navigate(path);
  };

  // Countdown → auto-dismiss when idle; first interaction cancels it.
  useEffect(() => {
    if (!visible) return;
    engagedRef.current = false;
    setAutoCloseIn(12);
    const onEngage = () => { engagedRef.current = true; setAutoCloseIn(null); };
    window.addEventListener("pointerdown", onEngage, { once: true, capture: true });
    window.addEventListener("keydown", onEngage, { once: true, capture: true });
    const iv = setInterval(() => {
      if (engagedRef.current) { clearInterval(iv); return; }
      setAutoCloseIn((n) => {
        if (n === null) { clearInterval(iv); return null; }
        if (n <= 1) { clearInterval(iv); dismiss(); return null; }
        return n - 1;
      });
    }, 1000);
    return () => {
      clearInterval(iv);
      window.removeEventListener("pointerdown", onEngage, { capture: true });
      window.removeEventListener("keydown", onEngage, { capture: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible || !user) return null;

  const name = user.first_name || user.name || user.explorer_name || "Explorer";
  const isFounder = Boolean(user.founding_member);
  const isParent = user.role === "parent";
  const isKid = user.role === "child_explorer";
  // A "solo explorer" is a teen/young-adult who signed up for themselves
  // (they may or may not later add kids of their own). Per product spec
  // they DON'T see the Add-Explorer prompt, they should land on the
  // homepage and get nudged to complete their profile via a separate banner.
  const isSoloExplorer = (user.account_type || "").toLowerCase() === "solo_explorer";
  const isAdmin = isAdminUser(user);

  // Subtle "closing soon" hint, shown only in the last few idle seconds.
  const autoCloseHint = autoCloseIn !== null && autoCloseIn <= 6 ? (
    <p className="text-center text-white/45 text-[10px] font-nunito mt-2" data-testid="welcome-autoclose-hint">
      Closing in {autoCloseIn}s, tap anywhere to stay
    </p>
  ) : null;

  // ─── PARENT FLOW ──────────────────────────────────────────────────
  // Two-step: welcome → "Add your first Explorer" hero CTA.
  // Emphasises that a parent account alone doesn't unlock kids' games; a
  // child profile must be created first.
  if (isParent && !isAdmin && !isSoloExplorer) {
    const STEPS = [
      {
        title: `Welcome, ${name}!`,
        subtitle: isFounder ? `You're officially Founder #${user.founding_position || ""}` : "Your parent account is ready",
        content: isFounder
          ? "You're in before anyone else. Now let's add your first kid explorer so your whole family can start learning, playing, and collecting Stars together."
          : "Let's add your first kid explorer so your family can start learning, playing, and collecting Stars together.",
      },
      {
        title: "Add your first Explorer",
        subtitle: "It takes about 60 seconds",
        content: "Kids' games, AI Tutor, Arcade, and progress tracking all live on the explorer profile. You can add as many explorers as you have kids, each one gets their own stars, badges, and login.",
      },
    ];
    const s = STEPS[step];
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="welcome-new-user-modal">
        <div className="absolute inset-0 bg-black/80" onClick={dismiss} />
        <div className="relative w-full max-w-md rounded-3xl border-2 border-emerald-400/25 overflow-hidden"
          style={{ background: "linear-gradient(180deg, rgba(15,20,40,0.98), rgba(20,40,30,0.98))", boxShadow: "0 0 80px rgba(16,185,129,0.18), 0 25px 50px rgba(0,0,0,0.5)" }}>
          <button onClick={dismiss} className="absolute top-4 right-4 z-10 text-white/30 hover:text-white/60 transition-colors" data-testid="welcome-close-btn">
            <X size={18} />
          </button>
          <div className="absolute top-0 left-0 right-0 h-32" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(16,185,129,0.25), transparent 70%)" }} />
          <div className="flex justify-center gap-2 pt-5 relative z-10">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? "w-8 bg-emerald-400" : i < step ? "w-4 bg-emerald-400/50" : "w-4 bg-white/15"}`} />
            ))}
          </div>
          <div className="p-6 pt-4 relative z-10">
            {step === 0 && (
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <IsaiahMascotCore pose="celebrating" size="full-body" glow testId="welcome-isaiah" />
                  {isFounder && (
                    <div className="absolute -top-1 -right-1 w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/40 border-2 border-amber-200/50">
                      <Crown size={16} className="text-white" fill="white" />
                    </div>
                  )}
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/30">
                    <span className="text-emerald-300 text-[10px] font-bold">Your Guide</span>
                  </div>
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/25 to-teal-500/25 border border-emerald-400/40 flex items-center justify-center">
                  <UserPlus size={32} className="text-emerald-300" strokeWidth={2} />
                </div>
              </div>
            )}
            <div className="text-center mb-4">
              <h2 className="text-xl font-fredoka font-bold text-white mb-1" data-testid="welcome-title">{s.title}</h2>
              <p className={`text-xs font-nunito ${isFounder && step === 0 ? "text-amber-300" : "text-white/45"}`}>{s.subtitle}</p>
            </div>
            <p className="text-white/65 text-sm text-center leading-relaxed font-nunito mb-5">{s.content}</p>

            {step === 1 && (
              <ul className="space-y-2 mb-5 text-white/70 text-[13px]">
                <li className="flex items-start gap-2"><span className="text-emerald-300 mt-0.5">✓</span> Grade & age-tailored content per child</li>
                <li className="flex items-start gap-2"><span className="text-emerald-300 mt-0.5">✓</span> Separate kid-safe login (their own username, no email needed)</li>
                <li className="flex items-start gap-2"><span className="text-emerald-300 mt-0.5">✓</span> You see every progress report from Parent Corner</li>
              </ul>
            )}

            <div className="flex gap-2">
              {step === 0 ? (
                <button onClick={() => setStep(1)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-fredoka font-bold text-sm transition-all hover:scale-[1.02]"
                  style={{ background: "linear-gradient(135deg, #10B981, #14B8A6)", boxShadow: "0 4px 20px rgba(16,185,129,0.35)" }}
                  data-testid="welcome-next-btn">
                  Next <ChevronRight size={16} />
                </button>
              ) : (
                <button onClick={() => goExplore("/parent-corner")}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-fredoka font-bold text-sm transition-all hover:scale-[1.02]"
                  style={{ background: "linear-gradient(135deg, #10B981, #14B8A6, #06B6D4)", boxShadow: "0 4px 20px rgba(16,185,129,0.35)" }}
                  data-testid="welcome-add-explorer-btn">
                  <UserPlus size={16} /> Add my first Explorer
                </button>
              )}
            </div>
            <button onClick={dismiss} className="w-full text-center text-white/30 hover:text-white/55 text-[10px] font-nunito mt-3 transition-colors" data-testid="welcome-skip-btn">
              {step === 0 ? "Skip for now, I'll do this later" : "I'll do this later, take me to the homepage"}
            </button>
            {autoCloseHint}
          </div>
        </div>
      </div>
    );
  }

  // ─── KID EXPLORER FLOW ────────────────────────────────────────────
  // Kids already get a dedicated flow via the `KidOnboarding` component
  // (their post-signup tour), but for kids who skip that or never land
  // on a kid-onboarding page, show a single reminder to poke their parent
  // to confirm the account so the family can start playing together.
  if (isKid) {
    const parentPendingConfirm = Boolean(user.pending_parent_confirmation);
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="welcome-new-user-modal" data-variant="kid">
        <div className="absolute inset-0 bg-black/80" onClick={dismiss} />
        <div className="relative w-full max-w-md rounded-3xl border-2 border-purple-400/25 overflow-hidden"
          style={{ background: "linear-gradient(180deg, rgba(15,20,40,0.98), rgba(30,20,50,0.98))", boxShadow: "0 0 80px rgba(139,92,246,0.2), 0 25px 50px rgba(0,0,0,0.5)" }}>
          <button onClick={dismiss} className="absolute top-4 right-4 z-10 text-white/30 hover:text-white/60 transition-colors" data-testid="welcome-close-btn">
            <X size={18} />
          </button>
          <div className="absolute top-0 left-0 right-0 h-32" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.25), transparent 70%)" }} />
          <div className="p-6 pt-8 relative z-10">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <IsaiahMascotCore pose="celebrating" size="full-body" glow testId="welcome-isaiah" />
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-purple-500/20 border border-purple-400/30">
                  <span className="text-purple-300 text-[10px] font-bold">Your Guide</span>
                </div>
              </div>
            </div>
            <div className="text-center mb-4">
              <h2 className="text-xl font-fredoka font-bold text-white mb-1" data-testid="welcome-title">Welcome, {name}!</h2>
              <p className="text-purple-300 text-xs font-nunito">You're in!</p>
            </div>
            <p className="text-white/65 text-sm text-center leading-relaxed font-nunito mb-5">
              {parentPendingConfirm
                ? "Now make sure you tell your parent to confirm your account, so your whole family can start learning, playing, and collecting Stars together."
                : "Ready for an adventure? Jump into games, earn Stars, and explore every world we've built just for you."}
            </p>
            <button onClick={() => goExplore("/my-world")}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-fredoka font-bold text-sm transition-all hover:scale-[1.02]"
              style={{ background: "linear-gradient(135deg, #8B5CF6, #EC4899)", boxShadow: "0 4px 20px rgba(139,92,246,0.35)" }}
              data-testid="welcome-kid-continue-btn">
              <Sparkles size={16} /> Let's Go!
            </button>
            {autoCloseHint}
          </div>
        </div>
      </div>
    );
  }

  // ─── SOLO EXPLORER FLOW ───────────────────────────────────────────
  // A single friendly welcome, no auto-push to "Add Explorer", just a
  // soft reminder that they can add kids later from Parent Corner if
  // they want the whole family on board.
  if (isSoloExplorer && !isAdmin) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="welcome-new-user-modal" data-variant="solo">
        <div className="absolute inset-0 bg-black/80" onClick={dismiss} />
        <div className="relative w-full max-w-md rounded-3xl border-2 border-cyan-400/25 overflow-hidden"
          style={{ background: "linear-gradient(180deg, rgba(15,20,40,0.98), rgba(10,35,45,0.98))", boxShadow: "0 0 80px rgba(34,211,238,0.2), 0 25px 50px rgba(0,0,0,0.5)" }}>
          <button onClick={dismiss} className="absolute top-4 right-4 z-10 text-white/30 hover:text-white/60 transition-colors" data-testid="welcome-close-btn">
            <X size={18} />
          </button>
          <div className="absolute top-0 left-0 right-0 h-32" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(34,211,238,0.22), transparent 70%)" }} />
          <div className="p-6 pt-8 relative z-10">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <IsaiahMascotCore pose="celebrating" size="full-body" glow testId="welcome-isaiah" />
                {isFounder && (
                  <div className="absolute -top-1 -right-1 w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/40 border-2 border-amber-200/50">
                    <Crown size={16} className="text-white" fill="white" />
                  </div>
                )}
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-400/30">
                  <span className="text-cyan-300 text-[10px] font-bold">Your Guide</span>
                </div>
              </div>
            </div>
            <div className="text-center mb-4">
              <h2 className="text-xl font-fredoka font-bold text-white mb-1" data-testid="welcome-title">Welcome, {name}!</h2>
              <p className={`text-xs font-nunito ${isFounder ? "text-amber-300" : "text-cyan-300/80"}`}>
                {isFounder ? `You're officially Founder #${user.founding_position || ""}` : "Your Solo Explorer account is ready"}
              </p>
            </div>
            <p className="text-white/70 text-sm text-center leading-relaxed font-nunito mb-5">
              You're in before anyone else. If you have a child, don't forget to add them so your whole family can start learning, playing, and collecting Stars together.
            </p>
            <div className="flex flex-col gap-2">
              <button onClick={() => goExplore("/profile/setup")}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-fredoka font-bold text-sm transition-all hover:scale-[1.02]"
                style={{ background: "linear-gradient(135deg, #22D3EE, #14B8A6)", boxShadow: "0 4px 20px rgba(34,211,238,0.35)" }}
                data-testid="welcome-solo-start-btn">
                <Sparkles size={16} /> Complete my profile
              </button>
              <button onClick={() => goExplore("/parent-corner")}
                className="w-full text-center text-cyan-200/70 hover:text-cyan-100 text-xs font-bold font-nunito py-2 transition-colors"
                data-testid="welcome-solo-add-kid-btn">
                I have a child, add them now →
              </button>
            </div>
            <button onClick={dismiss} className="w-full text-center text-white/25 hover:text-white/45 text-[10px] font-nunito mt-2 transition-colors" data-testid="welcome-skip-btn">
              Skip, I'll explore on my own
            </button>
            {autoCloseHint}
          </div>
        </div>
      </div>
    );
  }

  // ─── FALLBACK (admin, super_admin, legacy) ────────────────────────
  const STEPS = [
    {
      title: `Welcome, ${name}!`,
      subtitle: "Your account is ready",
      content: "You've officially joined Isaiah's World! A universe of learning, games, creativity, and adventure awaits you.",
      showIsaiah: true,
    },
    {
      title: "What's Waiting for You",
      subtitle: "Pick your first adventure",
      content: "Every activity earns you Stars and helps you level up. Here are the best places to start:",
      showWorlds: true,
    },
  ];
  const s = STEPS[step];

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="welcome-new-user-modal">
      <div className="absolute inset-0 bg-black/80" onClick={dismiss} />
      <div className="relative w-full max-w-md rounded-3xl border-2 border-purple-400/25 overflow-hidden"
        style={{ background: "linear-gradient(180deg, rgba(15,20,40,0.98), rgba(20,28,60,0.98))", boxShadow: "0 0 80px rgba(139,92,246,0.15), 0 25px 50px rgba(0,0,0,0.5)" }}>
        <button onClick={dismiss} className="absolute top-4 right-4 z-10 text-white/30 hover:text-white/60 transition-colors" data-testid="welcome-close-btn">
          <X size={18} />
        </button>
        <div className="absolute top-0 left-0 right-0 h-32" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.2), transparent 70%)" }} />
        <div className="flex justify-center gap-2 pt-5 relative z-10">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? "w-8 bg-purple-400" : i < step ? "w-4 bg-purple-400/50" : "w-4 bg-white/15"}`} />
          ))}
        </div>
        <div className="p-6 pt-4 relative z-10">
          {s.showIsaiah && (
            <div className="flex justify-center mb-4">
              <div className="relative">
                <IsaiahMascotCore pose="celebrating" size="full-body" glow testId="welcome-isaiah" />
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-purple-500/20 border border-purple-400/30">
                  <span className="text-purple-300 text-[10px] font-bold">Your Guide</span>
                </div>
              </div>
            </div>
          )}
          <div className="text-center mb-4">
            <h2 className="text-xl font-fredoka font-bold text-white mb-1" data-testid="welcome-title">{s.title}</h2>
            <p className="text-white/40 text-xs font-nunito">{s.subtitle}</p>
          </div>
          <p className="text-white/60 text-sm text-center leading-relaxed font-nunito mb-5">{s.content}</p>
          {s.showWorlds && (
            <div className="space-y-2 mb-5">
              {[
                { icon: Gamepad2, name: "Play a Game", desc: "Jump into Arcade City", color: "#8B5CF6", path: "/arcade" },
                { icon: Book, name: "Start a Lesson", desc: "Explore the Learning Forest", color: "#22C55E", path: "/learn" },
                { icon: Compass, name: "Go on an Adventure", desc: "AI-powered stories", color: "#F97316", path: "/adventures", comingSoon: true },
                { icon: Palette, name: "Create Art", desc: "Draw and design in Art Studio", color: "#EC4899", path: "/art-studio" },
              ].map((w) => {
                const blocked = w.comingSoon && !isAdminUser(user);
                return blocked ? (
                  <div key={w.path} className="w-full flex items-center gap-3 p-3 rounded-xl border border-white/5 bg-white/[0.02] opacity-40 cursor-not-allowed" data-testid={`welcome-action-${w.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${w.color}10`, border: `1px solid ${w.color}15` }}>
                      <w.icon size={18} style={{ color: w.color }} className="opacity-50" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/50 text-sm font-fredoka font-bold">{w.name}</p>
                      <p className="text-white/25 text-[10px] font-nunito">{w.desc}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded text-[8px] font-bold uppercase bg-orange-500/15 text-orange-400/70 border border-orange-500/15 shrink-0">Coming Soon</span>
                  </div>
                ) : (
                  <button key={w.path} onClick={() => goExplore(w.path)} className="w-full flex items-center gap-3 p-3 rounded-xl border border-white/8 bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/15 transition-all group text-left" data-testid={`welcome-action-${w.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${w.color}15`, border: `1px solid ${w.color}30` }}>
                      <w.icon size={18} style={{ color: w.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-fredoka font-bold">{w.name}</p>
                      <p className="text-white/40 text-[10px] font-nunito">{w.desc}</p>
                    </div>
                    <ChevronRight size={14} className="text-white/20 group-hover:text-white/50 transition-colors shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex gap-2">
            {step === 0 ? (
              <button onClick={() => setStep(1)} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-fredoka font-bold text-sm transition-all hover:scale-[1.02]" style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6, #EC4899)", boxShadow: "0 4px 20px rgba(139,92,246,0.3)" }} data-testid="welcome-next-btn">
                <Rocket size={16} /> See What's Waiting
              </button>
            ) : (
              <button onClick={() => goExplore("/")} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-fredoka font-bold text-sm transition-all hover:scale-[1.02]" style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6, #EC4899)", boxShadow: "0 4px 20px rgba(139,92,246,0.3)" }} data-testid="welcome-explore-btn">
                <Sparkles size={16} /> Start Exploring the Universe
              </button>
            )}
          </div>
          {step === 0 && (
            <button onClick={dismiss} className="w-full text-center text-white/25 hover:text-white/45 text-[10px] font-nunito mt-3 transition-colors" data-testid="welcome-skip-btn">
              Skip, I'll explore on my own
            </button>
          )}
          {autoCloseHint}
        </div>
      </div>
    </div>
  );
};

export default WelcomeNewUserModal;
